import { useState } from 'react';
import { saveVenueTable, deleteVenueTable, GetAdminVenueDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Table2, Users } from 'lucide-react';

type VTable = GetAdminVenueDataOutputType['tables'][0];
type Room = GetAdminVenueDataOutputType['rooms'][0];

const SHAPES = ['Round', 'Square', 'Rectangular', 'Oval', 'Other'];

function TableForm({ table, rooms, onSave, onCancel }: { table: Partial<VTable> | null; rooms: Room[]; onSave: (t: Partial<VTable>) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState<Partial<VTable>>(table || { tableNumber: '', roomId: '', shape: 'Rectangular', showChairs: true, active: true });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.tableNumber?.trim()) { toast.error('Table number required'); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20 col-span-full">
      <p className="font-semibold text-foreground text-sm">{form.id ? 'Edit Table' : 'Add Table'}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Table Number *</label>
          <Input value={form.tableNumber || ''} onChange={e => setForm(f => ({ ...f, tableNumber: e.target.value }))} placeholder="e.g. 1, 2A, EXT-3" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Room</label>
          <Select value={form.roomId || ''} onValueChange={v => setForm(f => ({ ...f, roomId: v }))}>
            <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
            <SelectContent>
              {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Shape</label>
          <Select value={form.shape || 'Rectangular'} onValueChange={v => setForm(f => ({ ...f, shape: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Min Capacity</label>
          <Input type="number" value={form.minCapacity ?? ''} onChange={e => setForm(f => ({ ...f, minCapacity: e.target.value ? parseInt(e.target.value) : null }))} placeholder="e.g. 2" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Max Capacity</label>
          <Input type="number" value={form.maxCapacity ?? ''} onChange={e => setForm(f => ({ ...f, maxCapacity: e.target.value ? parseInt(e.target.value) : null }))} placeholder="e.g. 4" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Width (cm)</label>
          <Input type="number" value={form.widthCm ?? ''} onChange={e => setForm(f => ({ ...f, widthCm: e.target.value ? parseInt(e.target.value) : null }))} placeholder="e.g. 90" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Depth (cm)</label>
          <Input type="number" value={form.depthCm ?? ''} onChange={e => setForm(f => ({ ...f, depthCm: e.target.value ? parseInt(e.target.value) : null }))} placeholder="e.g. 75" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
          <Input value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. window table" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch id={`sc-${form.id || 'new'}`} checked={form.showChairs ?? true} onCheckedChange={v => setForm(f => ({ ...f, showChairs: v }))} />
          <label htmlFor={`sc-${form.id || 'new'}`} className="text-xs text-muted-foreground cursor-pointer">Show chairs on layout</label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id={`ta-${form.id || 'new'}`} checked={form.active ?? true} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
          <label htmlFor={`ta-${form.id || 'new'}`} className="text-xs text-muted-foreground cursor-pointer">Active</label>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save Table'}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function VenueTablesTab({ tables, rooms, onRefresh }: { tables: VTable[]; rooms: Room[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<Partial<VTable> | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [filterRoom, setFilterRoom] = useState('all');

  const handleSave = async (form: Partial<VTable>) => {
    try {
      await saveVenueTable({ id: form.id, tableNumber: form.tableNumber!, roomId: form.roomId, minCapacity: form.minCapacity, maxCapacity: form.maxCapacity, shape: form.shape, widthCm: form.widthCm, depthCm: form.depthCm, showChairs: form.showChairs, active: form.active, notes: form.notes });
      toast.success(form.id ? 'Table updated' : 'Table added');
      setEditing(null);
      setShowNew(false);
      onRefresh();
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteVenueTable({ id }); toast.success('Table deleted'); onRefresh(); }
    catch (e: any) { toast.error(e.message); }
  };

  const filtered = filterRoom === 'all' ? tables : tables.filter(t => t.roomId === filterRoom);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Select value={filterRoom} onValueChange={setFilterRoom}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All rooms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rooms ({tables.length})</SelectItem>
              {rooms.map(r => {
                const count = tables.filter(t => t.roomId === r.id).length;
                return <SelectItem key={r.id} value={r.id}>{r.name} ({count})</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={() => { setShowNew(true); setEditing(null); }} className="gap-1.5"><Plus className="w-3.5 h-3.5" />Add Table</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {showNew && !editing && (
          <TableForm table={null} rooms={rooms} onSave={handleSave} onCancel={() => setShowNew(false)} />
        )}
        {filtered.map(t => (
          <div key={t.id}>
            {editing?.id === t.id ? (
              <TableForm table={editing} rooms={rooms} onSave={handleSave} onCancel={() => setEditing(null)} />
            ) : (
              <div className={`border rounded-xl p-3.5 ${!t.active ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Table2 className="w-4 h-4 text-primary" />
                    <span className="font-bold text-foreground">Table {t.tableNumber}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditing(t); setShowNew(false); }}><Pencil className="w-3 h-3" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Table {t.tableNumber}?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(t.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t.roomName}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                  {t.maxCapacity != null && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t.minCapacity != null ? `${t.minCapacity}–${t.maxCapacity}` : `up to ${t.maxCapacity}`} ppl</span>}
                  {t.shape && <span className="capitalize">{t.shape}</span>}
                  {t.widthCm && t.depthCm && <span>{t.widthCm}×{t.depthCm}cm</span>}
                  {t.showChairs && <span>🪑 chairs</span>}
                  {t.notes && <span className="text-foreground/70">{t.notes}</span>}
                </div>
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && !showNew && (
          <div className="col-span-full text-center py-12 text-muted-foreground border border-dashed border-border rounded-xl">
            <Table2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tables configured yet.</p>
            <p className="text-xs mt-1">Add rooms first, then assign tables to each room.</p>
          </div>
        )}
      </div>
    </div>
  );
}
